import React, { useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { BoldBodyTextTW } from '../../../../../tailwindComponents/bodyText/BodyTextTW';
import { Grid, SelectField } from '../../../../../ui-toolkit';
import { SigningAppointmentDescriptionTooltip } from '../../../signingAppointments/SigningAppointmentDescriptionTooltip';
import {
  useListStatusesQuery,
  useUpdateMutation,
} from '../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { useSelectAppointmentFromPathParam } from '../../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import { getStatusDescriptions } from '../../../../../types/signingAppointment/ISigningAppointmentStatusResponse';
import { ISigningAppointmentEditFormData } from '../../../../../types/signingAppointment/ISigningAppointmentEditFormData';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../types/shared/UUID';
import { checkForMutationSuccess } from '../../../../../API/checkForMutationSuccess';
import { CancelOrSubmit } from '../CancelOrSubmit';
import { computeStatusOptions } from 'types/signingAppointment/ComputeStatusOptions';

// This is used to edit a DSS appointment that is *not* a Draft.
// An email has been sent for these appointments.
export const EditDSSAppointmentForm: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId: UUID }>();
  const [updateSigningAppointment] = useUpdateMutation();

  const onSaveEditions = useCallback(
    async (data: ISigningAppointmentEditFormData) => {
      const updateResponse = await updateSigningAppointment({
        id: appointmentId,
        orderId,
        data,
      });
      if (checkForMutationSuccess(updateResponse, 'updating signing appointment')) {
        onClose();
      }
    },
    [updateSigningAppointment, orderId, appointmentId, onClose]
  );

  const { data: signingStatuses = [] } = useListStatusesQuery();
  const statusOptions = useMemo(
    () => computeStatusOptions(signingStatuses, appointmentFromPath),
    [appointmentFromPath, signingStatuses]
  );
  const statusDescriptions = useMemo(() => getStatusDescriptions(signingStatuses), [signingStatuses]);

  if (!appointmentFromPath) {
    return null;
  }
  return (
    <Formik
      initialValues={appointmentFromPath}
      validationSchema={DssAppointmentValidationSchema}
      onSubmit={onSaveEditions}
      enableReinitialize
    >
      <Form>
        <div className={'flex flex-row pb-5'}>
          <CalendarIcon className={'mr-2 h-5 w-5'} />
          <BoldBodyTextTW>Edit Appointment Details</BoldBodyTextTW>
          <CancelOrSubmit onClose={onClose} />
        </div>

        <div className={'space-y-4'}>
          <Grid columnCount={2} templateColumns={'.5fr .5fr'}>
            <SelectField
              data-test-id={`${appointmentFromPath?.id}-scheduling-tab-status-select`}
              name={'status'}
              label="Status"
              placeholder={`Select a status`}
              options={statusOptions}
              formatOptionLabel={(option: any) => {
                return (
                  <SigningAppointmentDescriptionTooltip
                    type={'status'}
                    option={option}
                    optionDescriptions={statusDescriptions}
                  />
                );
              }}
            />
          </Grid>
        </div>
      </Form>
    </Formik>
  );
};

const DssAppointmentValidationSchema = yup.object().shape({
  status: yup.string().required(),
});
